import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { COLLAB_IMG, HERO_IMAGES, CATEGORY_IMAGES } from "../mock";
import CORPORATE_TRAINING_HERO_IMG from "../assets/corporate-training-hero.png";
import CUSTOMIZED_TECH_PROGRAMS_IMG from "../assets/customized-tech-programs.jpg";
import AI_GENAI_VISUAL_IMG from "../assets/ai-genai-visual.jpg";
import HERO_LIGHT_BG_IMG from "../assets/hero-light-bg.jpg";
import {
  Star, Layers, Clock, Users, Brain, Cloud, ShieldCheck, Settings2,
  Video, Building2, Wrench, Layers3,
  ArrowRight, CheckCircle2
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#8BD835]"/>
    {children}
  </span>
);

const OUTCOMES = [
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Customized learning experiences designed around enterprise requirements." },
  { icon: Layers, value: "500+", label: "Enterprise Batches Delivered", desc: "Training programs delivered across technologies, teams, and business functions." },
  { icon: Clock, value: "100,000+", label: "Learning Hours Delivered", desc: "Practical learning experiences led by experienced industry trainers." },
  { icon: Users, value: "100+", label: "Enterprise Trainers", desc: "Access to subject matter experts across emerging technology domains." }
];

const TECH_AREAS = [
  { icon: Brain, image: AI_GENAI_VISUAL_IMG, title: "AI & Generative AI", desc: "Practical programs covering Artificial Intelligence, Generative AI, Machine Learning, Prompt Engineering, AI Governance, and enterprise use cases." },
  { icon: Cloud, image: CATEGORY_IMAGES.cloud, title: "Cloud & Data", desc: "Role-based training across cloud platforms, data engineering, analytics, and modern technology environments." },
  { icon: ShieldCheck, image: CATEGORY_IMAGES.security, title: "Cybersecurity", desc: "Training programs covering cybersecurity concepts, security practices, risk, compliance, and enterprise requirements." },
  { icon: Settings2, image: CUSTOMIZED_TECH_PROGRAMS_IMG, title: "Customized Technology Programs", desc: "Training designed around your technology stack, team roles, business priorities, and specific learning requirements." }
];

const TECH_AREA_COLORS = {
  blue: { badge: "bg-[#2F6FF0]", ring: "border-[#2F6FF0]/30", text: "text-[#2F6FF0]", panel: "bg-[#EEF3FF] border-[#15348C]/10", hover: "sg-card--blue" },
  teal: { badge: "bg-[#0F9D8B]", ring: "border-[#0F9D8B]/30", text: "text-[#0F9D8B]", panel: "bg-[#EAFBF6] border-[#0F9D8B]/10", hover: "sg-card--teal" },
  orange: { badge: "bg-[#8BD835]", ring: "border-[#8BD835]/30", text: "text-[#4F9622]", panel: "bg-[#FFF3E6] border-[#8BD835]/15", hover: "sg-card--orange" }
};

const CloudDataIllustration = () => (
  <svg viewBox="0 0 200 160" className="w-32 h-28 lg:w-40 lg:h-32 shrink-0" fill="none">
    <ellipse cx="100" cy="146" rx="72" ry="8" fill="#DCEBFF"/>
    <g opacity="0.95">
      <rect x="26" y="78" width="32" height="52" rx="6" fill="#BFD7FF"/>
      <ellipse cx="42" cy="78" rx="16" ry="6.5" fill="#9CC0FF"/>
      <rect x="140" y="86" width="30" height="44" rx="6" fill="#BFD7FF"/>
      <ellipse cx="155" cy="86" rx="15" ry="6" fill="#9CC0FF"/>
    </g>
    <path d="M62 92c-15 0-27-11.5-27-25.7 0-12.8 9.6-23.5 22.4-25.4C61.7 27.4 74.4 18 89 18c17 0 31 12.8 32.9 29 12.9 1.6 22.9 12.5 22.9 25.7 0 14.2-12 25.7-27 25.7H62z" fill="#2F6FF0"/>
    <path d="M100 54v34m0 0l-11-11m11 11l11-11" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CybersecurityIllustration = () => (
  <svg viewBox="0 0 200 180" className="w-28 h-28 lg:w-36 lg:h-32 shrink-0" fill="none">
    <circle cx="100" cy="92" r="72" fill="#CFF6EC" opacity="0.55"/>
    <path d="M100 26l48 17v36c0 36-20.5 61-48 72-27.5-11-48-36-48-72V43l48-17z" fill="#0F9D8B"/>
    <rect x="79" y="90" width="42" height="34" rx="7" fill="#fff"/>
    <path d="M87 90V79a13 13 0 0126 0v11" stroke="#fff" strokeWidth="7" fill="none" strokeLinecap="round"/>
    <circle cx="100" cy="106" r="5.5" fill="#0F9D8B"/>
  </svg>
);

const DELIVERY_FORMATS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive instructor-led sessions delivered online for teams across different locations." },
  { icon: Building2, title: "Classroom Training", desc: "Focused, instructor-led programs conducted at your location or a preferred training venue." },
  { icon: Wrench, title: "Hands-On Workshops", desc: "Practical workshops with labs, exercises, and real-world scenarios." },
  { icon: Layers3, title: "Blended Learning Programs", desc: "A combination of instructor-led sessions, practical labs, self-paced learning, and workshops." }
];

const CorporateTrainingPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundImage: `url(${HERO_LIGHT_BG_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="relative max-w-7xl mx-auto px-4 py-14 lg:py-16 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-white border border-[#15348C]/10 px-4 py-2 rounded-full mb-7 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#6BBE32] sg-pulse"/>
              ENTERPRISE TRAINING PROGRAMS
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold leading-[1.12] text-[#0B1E4D]">Enterprise Training That Aligns with Your Technology Priorities</h1>
            <p className="mt-5 text-gray-600 text-base lg:text-lg leading-relaxed max-w-xl">
              Customized instructor-led programs that help organizations strengthen capabilities across AI, Generative AI, cloud, data, cybersecurity, and emerging technologies.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Talk to Our Expert <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="relative">
              <img
                src={CORPORATE_TRAINING_HERO_IMG}
                alt="Enterprise corporate training session"
                className="w-full h-[340px] lg:h-[420px] object-cover rounded-2xl shadow-2xl ring-1 ring-black/5"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0A1F5C]/40 via-transparent to-transparent"/>
              <div className="absolute -bottom-7 -left-6 sm:left-0 bg-white rounded-xl shadow-xl p-4 pr-6 flex items-center gap-3.5 max-w-[240px]">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                  <Star size={18} className="text-[#6BBE32]"/>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#15348C] leading-none">98%</div>
                  <div className="text-[11px] text-gray-500 mt-1">Client Satisfaction</div>
                </div>
              </div>
              <div className="absolute -top-5 -right-4 bg-white rounded-xl shadow-xl px-4 py-3 flex items-center gap-2.5">
                <Users size={16} className="text-[#4F9622]"/>
                <div className="text-xs font-bold text-[#15348C]">100+ Enterprise Trainers</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Technology areas */}
      <section className="relative bg-[#F8F9FA] py-12 lg:py-14 overflow-hidden">
        <div
          className="absolute top-4 left-0 w-40 h-40 opacity-70 pointer-events-none hidden lg:block"
          style={{ backgroundImage: "radial-gradient(#CBD5E1 1.6px, transparent 1.6px)", backgroundSize: "16px 16px" }}
        />
        <svg className="absolute top-0 right-0 w-72 h-56 opacity-40 pointer-events-none hidden lg:block" viewBox="0 0 300 220" fill="none">
          <path d="M300 20C230 40 180 10 120 40S20 90 -10 60" stroke="#B9CBFA" strokeWidth="1.5"/>
          <path d="M300 60C230 80 180 50 120 80S20 130 -10 100" stroke="#CFE0FF" strokeWidth="1.5"/>
          <path d="M300 100C230 120 180 90 120 120S20 170 -10 140" stroke="#E2ECFF" strokeWidth="1.5"/>
        </svg>
        <div className="relative max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>WHERE SKILLSGAIN DELIVERS IMPACT</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold leading-tight">
                <span className="text-[#0B1E4D]">Enterprise Training Across Critical </span>
                <span className="text-[#2F6FF0]">Technology Areas</span>
              </h2>
              <p className="mt-3 text-gray-600">Programs designed around the technologies and capabilities organizations need today.</p>
              <span className="mt-3 inline-block w-10 h-0.5 rounded-full bg-[#2F6FF0]"/>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-5 gap-6 items-stretch">
            {/* 01 — AI & Generative AI (large feature card) */}
            <Reveal variant="up" className="lg:col-span-3">
              <div className="sg-card group relative rounded-2xl overflow-hidden h-full min-h-[420px] shadow-sm bg-[#0A1330] flex flex-col lg:flex-row">
                <div className="flex-1 p-8 lg:p-10 flex flex-col">
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${TECH_AREA_COLORS.blue.badge} text-white font-bold text-sm`}>01</span>
                    <span className="w-10 h-10 flex items-center justify-center rounded-full border border-white/40">
                      <Brain size={18} className="text-white"/>
                    </span>
                  </div>
                  <h3 className="mt-6 text-3xl font-bold text-white leading-tight">{TECH_AREAS[0].title}</h3>
                  <span className={`mt-4 w-10 h-0.5 block ${TECH_AREA_COLORS.blue.badge}`}/>
                  <p className="mt-4 text-gray-300 text-sm leading-relaxed max-w-md">{TECH_AREAS[0].desc}</p>
                  <div className="mt-auto pt-8">
                    <Link to="/contact" className={`sg-btn-glow inline-flex items-center gap-2 ${TECH_AREA_COLORS.blue.badge} hover:opacity-90 text-white font-semibold px-5 py-3 rounded-lg transition-opacity text-sm`}>
                      Explore Programs <ArrowRight size={16}/>
                    </Link>
                  </div>
                </div>
                <div className="relative h-56 lg:h-auto lg:w-[42%] shrink-0">
                  <img src={TECH_AREAS[0].image} alt={TECH_AREAS[0].title} className="absolute inset-0 w-full h-full object-cover object-right"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1330] via-[#0A1330]/10 to-transparent lg:hidden"/>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A1330] via-[#0A1330]/5 to-transparent hidden lg:block"/>
                </div>
              </div>
            </Reveal>

            {/* 02 & 03 — right-hand stacked cards */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <Reveal variant="up" delay={80}>
                <div className={`sg-card ${TECH_AREA_COLORS.blue.hover} relative rounded-2xl overflow-hidden p-6 h-full min-h-[196px] flex items-center gap-4 border ${TECH_AREA_COLORS.blue.panel}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${TECH_AREA_COLORS.blue.badge} text-white font-bold text-sm`}>02</span>
                      <span className={`w-10 h-10 flex items-center justify-center rounded-full border bg-white ${TECH_AREA_COLORS.blue.ring}`}>
                        <Cloud size={18} className={TECH_AREA_COLORS.blue.text}/>
                      </span>
                    </div>
                    <h3 className="mt-4 text-xl font-bold text-[#15348C]">{TECH_AREAS[1].title}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{TECH_AREAS[1].desc}</p>
                  </div>
                  <CloudDataIllustration/>
                </div>
              </Reveal>
              <Reveal variant="up" delay={160}>
                <div className={`sg-card ${TECH_AREA_COLORS.teal.hover} relative rounded-2xl overflow-hidden p-6 h-full min-h-[196px] flex items-center gap-4 border ${TECH_AREA_COLORS.teal.panel}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${TECH_AREA_COLORS.teal.badge} text-white font-bold text-sm`}>03</span>
                      <span className={`w-10 h-10 flex items-center justify-center rounded-full border bg-white ${TECH_AREA_COLORS.teal.ring}`}>
                        <ShieldCheck size={18} className={TECH_AREA_COLORS.teal.text}/>
                      </span>
                    </div>
                    <h3 className="mt-4 text-xl font-bold text-[#15348C]">{TECH_AREAS[2].title}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{TECH_AREAS[2].desc}</p>
                  </div>
                  <CybersecurityIllustration/>
                </div>
              </Reveal>
            </div>
          </div>

          {/* 04 — Customized Technology Programs (banner) */}
          <Reveal variant="up" delay={240}>
            <div className={`sg-card ${TECH_AREA_COLORS.orange.hover} mt-6 relative rounded-2xl overflow-hidden border ${TECH_AREA_COLORS.orange.panel} shadow-lg shadow-[#8BD835]/10`}>
              <div className="flex flex-col lg:flex-row items-center">
                <div className="flex-1 w-full p-5 lg:pl-8 lg:pr-6 lg:py-6">
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg ${TECH_AREA_COLORS.orange.badge} text-white font-bold text-sm`}>04</span>
                    <span className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border bg-white ${TECH_AREA_COLORS.orange.ring}`}>
                      <Settings2 size={18} className={TECH_AREA_COLORS.orange.text}/>
                    </span>
                    <h3 className="text-xl lg:text-2xl font-bold text-[#15348C]">{TECH_AREAS[3].title}</h3>
                  </div>
                  <p className="mt-2.5 text-sm text-gray-600 leading-relaxed max-w-lg">{TECH_AREAS[3].desc}</p>
                  <div className="mt-4">
                    <Link to="/contact" className={`sg-btn-glow inline-flex items-center gap-2 ${TECH_AREA_COLORS.orange.badge} hover:opacity-90 text-white font-semibold px-5 py-2.5 rounded-lg transition-opacity text-sm`}>
                      Explore Programs <ArrowRight size={16}/>
                    </Link>
                  </div>
                </div>
                <div className="w-full lg:w-[55%] shrink-0">
                  <img src={TECH_AREAS[3].image} alt={TECH_AREAS[3].title} className="w-full h-40 lg:h-36 object-cover"/>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Flexible delivery */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img src={COLLAB_IMG} alt="Enterprise training delivery" className="rounded-2xl shadow-xl ring-1 ring-black/5 w-full h-[440px] object-cover"/>
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>FLEXIBLE DELIVERY</Eyebrow>
            <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Choose the Training Format That Works for Your Teams</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">Deliver learning experiences across locations and teams through formats suited to your organizational requirements.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {DELIVERY_FORMATS.map(d => (
                <div key={d.title} className="sg-card flex gap-3.5 bg-white border border-gray-200/80 rounded-xl p-5 shadow-sm">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                    <d.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div>
                    <div className="font-bold text-[#15348C] text-sm">{d.title}</div>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Measurable outcomes — by the numbers */}
      <section className="bg-[#FFFAF0] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>MEASURABLE OUTCOMES</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Training That Delivers More Than Course Completion</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OUTCOMES.map((o, i) => (
              <Reveal key={o.label} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 border-t-4 border-t-transparent hover:border-t-[#6BBE32] rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <o.icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <div className="text-4xl font-bold text-[#15348C] tracking-tight">{o.value}</div>
                  <div className="text-sm font-semibold text-[#15348C] mt-2">{o.label}</div>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Business alignment statement */}
      <section className="bg-[#F8F9FA] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img
              src={HERO_IMAGES[1]}
              alt="Enterprise learning aligned to business requirements"
              className="w-full h-[380px] object-cover rounded-2xl shadow-xl ring-1 ring-black/5"
            />
          </Reveal>
          <Reveal variant="right" delay={80}>
            <div className="w-12 h-1 bg-[#8BD835] rounded-full mb-8"/>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#15348C] leading-snug">Enterprise Learning That Supports Real Business Requirements</h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Skillsgain works with organizations to plan and deliver training programs aligned with their technology priorities, team requirements, and business objectives.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              From AI and Generative AI to cloud, data, cybersecurity, and other emerging technologies, our programs combine experienced trainers, practical learning, and customized delivery models.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR LEARNING &amp; BUSINESS LEADERS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Plan Your Enterprise Training Program</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Tell us about your technology priorities, team requirements, and learning objectives. Our experts will help you identify the right training approach for your organization.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Customized training approach", "Expert-led programs", "Practical learning experiences"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request a Corporate Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default CorporateTrainingPage;
